import React, { useState, useEffect } from "react";
import styles from "./css/GeneralParametersManagementPage.module.css";
import { Tabs, Tab, Box } from "@mui/material";
import { InputField } from "@/components/inputFields/inputFields";
import { Button } from "@/components/Buttons";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { getGeneralAttributes, updateGeneralAttributes } from "@/api/generalAttributes";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`general-params-tabpanel-${index}`}
      aria-labelledby={`general-params-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const GeneralParametersManagementPage: React.FC = () => {
  const user = useSelector((state: RootState) => state.user.user);
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Tooltip definitions
  const tooltips = {
    // System Constants
    fuelFactor: "פקטור התאמת צריכת דלק (ברירת מחדל: 0.84) - שדה בסיס המשפיע על חישובי צריכת דלק ופליטות CO2 בפרמטרי עיר",
    facadeThreshold: "סף זיהוי כיוון חזית במודלים תלת מימדיים - שדה בסיס המשפיע על חישוב שטחי חזיתות בזמן עיבוד קבצי 3D",
    ratingResidential: "דירוג אנרגטי ברירת מחדל למגורים - שדה בסיס המשפיע על חישובי צריכת אנרגיה של בניינים חדשים",
    ratingCommercial: "דירוג אנרגטי ברירת מחדל למסחרי - שדה בסיס המשפיע על חישובי צריכת אנרגיה של בניינים חדשים",
    ratingPublic: "דירוג אנרגטי ברירת מחדל לציבורי - שדה בסיס המשפיע על חישובי צריכת אנרגיה של בניינים חדשים",
    ratingIndustrial: "דירוג אנרגטי ברירת מחדל לתעשייה - שדה בסיס המשפיע על חישובי צריכת אנרגיה של בניינים חדשים",
    
    // Energy Coefficients
    energyResidential: "מקדם צריכת אנרגיה למ״ר מגורים (KWh/m²) - שדה בסיס המשפיע על חישובי צריכת אנרגיה מצופה בפרויקטים",
    energyCommercial: "מקדם צריכת אנרגיה למ״ר מסחרי (KWh/m²) - שדה בסיס המשפיע על חישובי צריכת אנרגיה מצופה בפרויקטים",
    energyIndustrial: "מקדם צריכת אנרגיה למ״ר תעשייה (KWh/m²) - שדה בסיס המשפיע על חישובי צריכת אנרגיה מצופה בפרויקטים",
    energyPublic: "מקדם צריכת אנרגיה למ״ר ציבורי (KWh/m²) - שדה בסיס המשפיע על חישובי צריכת אנרגיה מצופה בפרויקטים",
    consumptionPerson: "צריכה לנפש (KW/שנה) - מחושב אוטומטית לפי הנוסחה: צריכת חשמל משקי בית לאומית ÷ אוכלוסיית ישראל. ניתן לעדכן ידנית במידת הצורך",
    
    // Conversion & Production
    convRoof: "יחס המרה לגגות - שדה בסיס המשפיע על חישוב כמות פאנלים סולאריים שניתן להתקין על גגות",
    convFacades: "יחס המרה לחזיתות - שדה בסיס המשפיע על חישוב כמות פאנלים סולאריים שניתן להתקין על חזיתות",
    prodRoof: "יחס ייצור לגגות - שדה בסיס המשפיע על חישוב ייצור אנרגיה משטחי גג",
    prodFacades: "יחס ייצור לחזיתות - שדה בסיס המשפיע על חישוב ייצור אנרגיה משטחי חזית",
    
    // Vehicles & Emissions
    carAge: "גיל ממוצע רכב בישראל - שדה בסיס המשפיע על חישובי פליטות רכב",
    motorizationRate: "רמת מינוע לאומית (רכבים לאלף תושבים) - שדה בסיס המשפיע על תחזית כמות רכבים בפרויקטים",
    vehicleGrowth: "קצב גידול שנתי ברכבים - שדה בסיס המשפיע על תחזית צמיחה ברכבים",
    evReplacementRate: "קצב החלפת רכבים חשמליים - שדה בסיס המשפיע על תחזית אימוץ רכבים חשמליים",
    vehicleCO2: "מקדם פליטת CO2 של רכבים - שדה בסיס המשפיע על חישובי פליטות רכבים",
    electricityCO2: "מקדם פליטת CO2 של חשמל - שדה בסיס המשפיע על חישובי פליטות מצריכת חשמל",
    gasolineConsumption: "צריכת דלק בנזין כללית בישראל - שדה בסיס המשפיע על חישובי צריכת דלק",
    
    // Population
    israelPop: "אוכלוסיית ישראל - שדה בסיס המשפיע על חישוב צריכה לנפש",
    householdPop: "מספר נפשות ממוצע במשק בית - שדה בסיס המשפיע על חישובי אוכלוסייה מתוכננת",
    popGrowth: "קצב גידול אוכלוסייה שנתי - שדה בסיס המשפיע על תחזית צמיחת אוכלוסייה בפרויקטים",
    
    // Charging Stations
    acStationDaily: "שירות יומי עמדת AC - מספר כלי רכב שעמדת טעינה AC יכולה לשרת ביום. שדה בסיס המשפיע על חישוב כמות עמדות AC נדרשת",
    dcStationDaily: "שירות יומי עמדת DC - מספר כלי רכב שעמדת טעינה DC יכולה לשרת ביום. שדה בסיס המשפיע על חישוב כמות עמדות DC נדרשת",
    dailyVehicle: "צריכת חשמל יומית לרכב - שדה בסיס המשפיע על חישוב צריכת חשמל כוללת מרכבים חשמליים",
    avgCharging: "טעינה ממוצעת - אחוז טעינה ממוצע לרכב. שדה בסיס המשפיע על חישובי תחזית טעינה",
    userVehiclesLocal: "רכבי משתמשים טוענים ברשת ציבורית מקומית - מספר רכבים המשתמשים בעמדות ציבוריות. שדה בסיס",
    localUsersRatio: "יחס משתמשים טוענים ברשות ציבורית מקומית (%) - אחוז מהמשתמשים הטוענים ברשת ציבורית. שדה בסיס",
    commutersRatio: "יחס יוממים חשמליים לרכבים בעיר - שדה בסיס המשפיע על חישוב עומס טעינה ביום",
    acRatio: "יחס עמדות AC (0-1) - יחס בין עמדות AC לסך העמדות. שדה בסיס קריטי המשפיע על חישוב כמות עמדות AC/DC נדרשות",
  };
  
  // System Constants
  const [fuelFactor, setFuelFactor] = useState<string | undefined>(undefined);
  const [facadeThreshold, setFacadeThreshold] = useState<string | undefined>(undefined);
  const [ratingResidential, setRatingResidential] = useState<string | undefined>(undefined);
  const [ratingCommercial, setRatingCommercial] = useState<string | undefined>(undefined);
  const [ratingPublic, setRatingPublic] = useState<string | undefined>(undefined);
  const [ratingIndustrial, setRatingIndustrial] = useState<string | undefined>(undefined);
  
  // Energy Coefficients
  const [energyResidential, setEnergyResidential] = useState<string | undefined>(undefined);
  const [energyCommercial, setEnergyCommercial] = useState<string | undefined>(undefined);
  const [energyIndustrial, setEnergyIndustrial] = useState<string | undefined>(undefined);
  const [energyPublic, setEnergyPublic] = useState<string | undefined>(undefined);
  const [consumptionPerson, setConsumptionPerson] = useState<string | undefined>(undefined);
  
  // Conversion & Production Ratios
  const [convRoof, setConvRoof] = useState<string | undefined>(undefined);
  const [convFacades, setConvFacades] = useState<string | undefined>(undefined);
  const [prodRoof, setProdRoof] = useState<string | undefined>(undefined);
  const [prodFacades, setProdFacades] = useState<string | undefined>(undefined);
  
  // Vehicles
  const [carAge, setCarAge] = useState<string | undefined>(undefined);
  const [motorizationRate, setMotorizationRate] = useState<string | undefined>(undefined);
  const [vehicleGrowth, setVehicleGrowth] = useState<string | undefined>(undefined);
  const [evReplacementRate, setEvReplacementRate] = useState<string | undefined>(undefined);
  const [vehicleCO2, setVehicleCO2] = useState<string | undefined>(undefined);
  const [electricityCO2, setElectricityCO2] = useState<string | undefined>(undefined);
  const [gasolineConsumption, setGasolineConsumption] = useState<string | undefined>(undefined);
  
  // Population
  const [israelPop, setIsraelPop] = useState<string | undefined>(undefined);
  const [householdPop, setHouseholdPop] = useState<string | undefined>(undefined);
  const [popGrowth, setPopGrowth] = useState<string | undefined>(undefined);
  
  // Charging Stations
  const [acStationDailyService, setAcStationDailyService] = useState<string | undefined>(undefined);
  const [dcStationDailyService, setDcStationDailyService] = useState<string | undefined>(undefined);
  const [dailyVehicleConsumption, setDailyVehicleConsumption] = useState<string | undefined>(undefined);
  const [averageCharging, setAverageCharging] = useState<string | undefined>(undefined);
  const [userVehiclesChargingInLocalPublic, setUserVehiclesChargingInLocalPublic] = useState<string | undefined>(undefined);
  const [localPublicChargingUsersRatio, setLocalPublicChargingUsersRatio] = useState<string | undefined>(undefined);
  const [electricCommutersRatio, setElectricCommutersRatio] = useState<string | undefined>(undefined);
  const [acStationsRatio, setAcStationsRatio] = useState<string | undefined>(undefined);
  
  // Check super admin access
  useEffect(() => {
    if (user.user_type !== "super_admin") {
      alert("רק מנהל על יכול לגשת לניהול נתונים כלל ארציים");
      window.history.back();
      return;
    }
  }, [user.user_type]);

  // Fetch general attributes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await getGeneralAttributes();
        console.log("General Attributes:", data);
        
        setFuelFactor(data.fuel_consumption_adjustment_factor?.toString());
        setFacadeThreshold(data.facade_direction_threshold?.toString());
        setRatingResidential(data.default_energy_rating_residential || "B");
        setRatingCommercial(data.default_energy_rating_commercial || "D");
        setRatingPublic(data.default_energy_rating_public || "D");
        setRatingIndustrial(data.default_energy_rating_industrial || "D");
        
        // Energy coefficients - nested in energy_consumption_coefficients
        const energyCoeffs = data.energy_consumption_coefficients || {};
        setEnergyResidential(energyCoeffs.sqm_for_residential?.toString());
        setEnergyCommercial(energyCoeffs.sqm_for_commercial?.toString());
        setEnergyIndustrial(energyCoeffs.sqm_for_industrial?.toString());
        setEnergyPublic(energyCoeffs.sqm_for_public?.toString());
        setConsumptionPerson(data.consumption_per_person?.toString());
        
        // Conversion and production ratios - nested objects
        const convRatio = data.conversion_ratio || {};
        const prodRatio = data.production_ratio || {};
        setConvRoof(convRatio.roof?.toString());
        setConvFacades(convRatio.facades?.toString());
        setProdRoof(prodRatio.roof?.toString());
        setProdFacades(prodRatio.facades?.toString());
        
        // Electric vehicles - nested in electric_vehicles object
        const electricVehicles = data.electric_vehicles || {};
        setCarAge(electricVehicles.car_avg_age?.toString());
        setMotorizationRate(electricVehicles.national_motorization_rate?.toString());
        setVehicleGrowth(electricVehicles.annual_vehicle_growth_rate?.toString());
        setEvReplacementRate(electricVehicles.electric_vehicle_replacement_rate_2024?.toString());
        setVehicleCO2(data.vehicle_co2_emission_factors?.toString());
        setElectricityCO2(data.electricity_co2_emission_factor?.toString());
        setGasolineConsumption(data.total_gasoline_fuel_consumption_israel?.toString());
        
        setIsraelPop(data.israel_population?.toString());
        setHouseholdPop(data.household_population?.toString());
        setPopGrowth(data.population_growth?.toString());
        
        // Charging Stations - nested inside charging_stations object
        const chargingStations = data.charging_stations || {};
        setAcStationDailyService(chargingStations.ac_station_daily_service?.toString());
        setDcStationDailyService(chargingStations.dc_station_daily_service?.toString());
        setDailyVehicleConsumption(chargingStations.daily_vehicle_consumption?.toString());
        setAverageCharging(chargingStations.average_charging?.toString());
        setUserVehiclesChargingInLocalPublic(chargingStations.user_vehicles_charging_in_local_public_network?.toString());
        setLocalPublicChargingUsersRatio(chargingStations.local_public_charging_users_ratio?.toString());
        setElectricCommutersRatio(chargingStations.electric_commuters_to_city_vehicles_ratio?.toString());
        setAcStationsRatio(chargingStations.ac_stations_ratio?.toString());
        
      } catch (error) {
        console.error("Error fetching general attributes:", error);
        alert("שגיאה בטעינת הנתונים הכלליים");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData: any = {};
      
      if (fuelFactor) updateData.fuel_consumption_adjustment_factor = parseFloat(fuelFactor);
      if (facadeThreshold) updateData.facade_direction_threshold = parseFloat(facadeThreshold);
      if (ratingResidential) updateData.default_energy_rating_residential = ratingResidential;
      if (ratingCommercial) updateData.default_energy_rating_commercial = ratingCommercial;
      if (ratingPublic) updateData.default_energy_rating_public = ratingPublic;
      if (ratingIndustrial) updateData.default_energy_rating_industrial = ratingIndustrial;
      
      if (energyResidential) updateData.energy_consumption_sqm_for_residential = parseFloat(energyResidential);
      if (energyCommercial) updateData.energy_consumption_sqm_for_commercial = parseFloat(energyCommercial);
      if (energyIndustrial) updateData.energy_consumption_sqm_for_industrial = parseFloat(energyIndustrial);
      if (energyPublic) updateData.energy_consumption_sqm_for_public = parseFloat(energyPublic);
      // Don't send consumption_per_person - it's calculated automatically on the backend
      
      if (convRoof) updateData.conversion_ratio_roof = parseFloat(convRoof);
      if (convFacades) updateData.conversion_ratio_facades = parseFloat(convFacades);
      if (prodRoof) updateData.production_ratio_roof = parseFloat(prodRoof);
      if (prodFacades) updateData.production_ratio_facades = parseFloat(prodFacades);
      
      if (carAge) updateData.car_avg_age = parseFloat(carAge);
      if (motorizationRate) updateData.national_motorization_rate = parseFloat(motorizationRate);
      if (vehicleGrowth) updateData.annual_vehicle_growth_rate = parseFloat(vehicleGrowth);
      if (evReplacementRate) updateData.electric_vehicle_replacement_rate_2024 = parseFloat(evReplacementRate);
      if (vehicleCO2) updateData.vehicle_co2_emission_factors = parseFloat(vehicleCO2);
      if (electricityCO2) updateData.electricity_co2_emission_factor = parseFloat(electricityCO2);
      if (gasolineConsumption) updateData.total_gasoline_fuel_consumption_israel = parseFloat(gasolineConsumption);
      
      if (israelPop) updateData.israel_population = parseFloat(israelPop);
      if (householdPop) updateData.household_population = parseFloat(householdPop);
      if (popGrowth) updateData.population_growth = parseFloat(popGrowth);
      
      // Charging Stations
      if (acStationDailyService) updateData.ac_station_daily_service = parseFloat(acStationDailyService);
      if (dcStationDailyService) updateData.dc_station_daily_service = parseFloat(dcStationDailyService);
      if (dailyVehicleConsumption) updateData.daily_vehicle_consumption = parseFloat(dailyVehicleConsumption);
      if (averageCharging) updateData.average_charging = parseFloat(averageCharging);
      if (userVehiclesChargingInLocalPublic) updateData.user_vehicles_charging_in_local_public_network = parseFloat(userVehiclesChargingInLocalPublic);
      if (localPublicChargingUsersRatio) updateData.local_public_charging_users_ratio = parseFloat(localPublicChargingUsersRatio);
      if (electricCommutersRatio) updateData.electric_commuters_to_city_vehicles_ratio = parseFloat(electricCommutersRatio);
      if (acStationsRatio) updateData.ac_stations_ratio = parseFloat(acStationsRatio);
      
      const result = await updateGeneralAttributes(updateData);
      console.log("Update result:", result);
      alert(result.message || "הנתונים הכלליים עודכנו בהצלחה");
      
    } catch (error: any) {
      console.error("Error updating general attributes:", error);
      alert(error.message || "שגיאה בעדכון הנתונים הכלליים");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className={styles.loading}>טוען...</div>;
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>ניהול נתונים כלל ארציים</h1>
      <p className={styles.subtitle}>פרמטרים גלובליים המשפיעים על כל הערים והאלטרנטיבות במערכת</p>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 2 }}>
        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
          <Tab label="קבועי מערכת" />
          <Tab label="מקדמי אנרגיה" />
          <Tab label="רכבים ופליטות" />
          <Tab label="אוכלוסייה" />
          <Tab label="יחסי המרה וייצור" />
          <Tab label="עמדות טעינה" />
        </Tabs>
      </Box>

      {/* Tab 0: System Constants */}
      <TabPanel value={currentTab} index={0}>
        <div className={styles.section}>
          <h3>קבועי מערכת</h3>
          <div className={styles.fieldsGrid}>
            <div className={styles.fieldGroup}>
              <label>פקטור התאמת צריכת דלק</label>
              <InputField
                inputType="number"
                data={fuelFactor || ""}
                setData={setFuelFactor}
                id="fuel-factor"
                placeholder="0.84"
                tooltip={tooltips.fuelFactor}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label>סף זיהוי כיוון חזית (3D)</label>
              <InputField
                inputType="number"
                data={facadeThreshold || ""}
                setData={setFacadeThreshold}
                id="facade-threshold"
                placeholder="0.9"
                tooltip={tooltips.facadeThreshold}
              />
            </div>
          </div>
        </div>
        
        <div className={styles.section}>
          <h3>דירוג אנרגטי ברירת מחדל</h3>
          <div className={styles.fieldsGrid}>
            <div className={styles.fieldGroup}>
              <label>מגורים</label>
              <InputField
                inputType="text"
                data={ratingResidential || ""}
                setData={setRatingResidential}
                id="rating-res"
                placeholder="B"
                tooltip={tooltips.ratingResidential}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label>מסחרי</label>
              <InputField
                inputType="text"
                data={ratingCommercial || ""}
                setData={setRatingCommercial}
                id="rating-com"
                placeholder="D"
                tooltip={tooltips.ratingCommercial}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label>ציבורי</label>
              <InputField
                inputType="text"
                data={ratingPublic || ""}
                setData={setRatingPublic}
                id="rating-pub"
                placeholder="D"
                tooltip={tooltips.ratingPublic}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label>תעשייה</label>
              <InputField
                inputType="text"
                data={ratingIndustrial || ""}
                setData={setRatingIndustrial}
                id="rating-ind"
                placeholder="D"
                tooltip={tooltips.ratingIndustrial}
              />
            </div>
          </div>
        </div>
      </TabPanel>

      {/* Tab 1: Energy Coefficients */}
      <TabPanel value={currentTab} index={1}>
        <div className={styles.section}>
          <h3>מקדמי צריכת אנרגיה למ״ר</h3>
          <div className={styles.fieldsGrid}>
            <div className={styles.fieldGroup}>
              <label>מגורים (KWh/m²)</label>
              <InputField
                inputType="number"
                data={energyResidential || ""}
                setData={setEnergyResidential}
                id="energy-res"
                placeholder="225"
                tooltip={tooltips.energyResidential}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label>מסחרי (KWh/m²)</label>
              <InputField
                inputType="number"
                data={energyCommercial || ""}
                setData={setEnergyCommercial}
                id="energy-com"
                placeholder="1125"
                tooltip={tooltips.energyCommercial}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label>תעשייה (KWh/m²)</label>
              <InputField
                inputType="number"
                data={energyIndustrial || ""}
                setData={setEnergyIndustrial}
                id="energy-ind"
                placeholder="900"
                tooltip={tooltips.energyIndustrial}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label>ציבורי (KWh/m²)</label>
              <InputField
                inputType="number"
                data={energyPublic || ""}
                setData={setEnergyPublic}
                id="energy-pub"
                placeholder="800"
                tooltip={tooltips.energyPublic}
              />
            </div>
          </div>
        </div>
        
        <div className={styles.section}>
          <h3>צריכה לנפש</h3>
          <div className={styles.fieldsGrid}>
            <div className={styles.fieldGroup}>
              <label>צריכה שנתית לנפש (KW/Year) - מחושב אוטומטית</label>
              <InputField
                inputType="number"
                data={consumptionPerson || ""}
                setData={setConsumptionPerson}
                id="consumption-person"
                placeholder="2124"
                tooltip={tooltips.consumptionPerson}
                disabled
              />
            </div>
          </div>
        </div>
      </TabPanel>

      {/* Tab 2: Vehicles & Emissions */}
      <TabPanel value={currentTab} index={2}>
        <div className={styles.section}>
          <h3>רכבים</h3>
          <div className={styles.fieldsGrid}>
            <div className={styles.fieldGroup}>
              <label>גיל ממוצע רכב (שנים)</label>
              <InputField
                inputType="number"
                data={carAge || ""}
                setData={setCarAge}
                id="car-age"
                placeholder="7.5"
                tooltip={tooltips.carAge}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label>רמת מינוע כלל ארצית</label>
              <InputField
                inputType="number"
                data={motorizationRate || ""}
                setData={setMotorizationRate}
                id="motorization"
                placeholder="417"
                tooltip={tooltips.motorizationRate}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label>גידול שנתי ברכבים (%)</label>
              <InputField
                inputType="number"
                data={vehicleGrowth || ""}
                setData={setVehicleGrowth}
                id="vehicle-growth"
                placeholder="0.036"
                tooltip={tooltips.vehicleGrowth}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label>קצב החלפה לרכבים חשמליים 2024 (%)</label>
              <InputField
                inputType="number"
                data={evReplacementRate || ""}
                setData={setEvReplacementRate}
                id="ev-rate"
                placeholder="5"
                tooltip={tooltips.evReplacementRate}
              />
            </div>
          </div>
        </div>
        
        <div className={styles.section}>
          <h3>פליטות CO2</h3>
          <div className={styles.fieldsGrid}>
            <div className={styles.fieldGroup}>
              <label>מקדם פליטת CO2 רכב</label>
              <InputField
                inputType="number"
                data={vehicleCO2 || ""}
                setData={setVehicleCO2}
                id="vehicle-co2"
                placeholder="2.28"
                tooltip={tooltips.vehicleCO2}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label>מקדם פליטת CO2 חשמל</label>
              <InputField
                inputType="number"
                data={electricityCO2 || ""}
                setData={setElectricityCO2}
                id="electricity-co2"
                placeholder="437"
                tooltip={tooltips.electricityCO2}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label>צריכת דלק בנזין כוללת בישראל</label>
              <InputField
                inputType="number"
                data={gasolineConsumption || ""}
                setData={setGasolineConsumption}
                id="gasoline"
                placeholder="8065728516"
                tooltip={tooltips.gasolineConsumption}
              />
            </div>
          </div>
        </div>
      </TabPanel>

      {/* Tab 3: Population */}
      <TabPanel value={currentTab} index={3}>
        <div className={styles.section}>
          <h3>נתוני אוכלוסייה</h3>
          <div className={styles.fieldsGrid}>
            <div className={styles.fieldGroup}>
              <label>אוכלוסיית ישראל</label>
              <InputField
                inputType="number"
                data={israelPop || ""}
                setData={setIsraelPop}
                id="israel-pop"
                placeholder="9449000"
                tooltip={tooltips.israelPop}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label>נפשות במשק בית</label>
              <InputField
                inputType="number"
                data={householdPop || ""}
                setData={setHouseholdPop}
                id="household-pop"
                placeholder="3.17"
                tooltip={tooltips.householdPop}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label>גידול אוכלוסייה שנתי (%)</label>
              <InputField
                inputType="number"
                data={popGrowth || ""}
                setData={setPopGrowth}
                id="pop-growth"
                placeholder="0.02"
                tooltip={tooltips.popGrowth}
              />
            </div>
          </div>
        </div>
      </TabPanel>

      {/* Tab 4: Conversion & Production Ratios */}
      <TabPanel value={currentTab} index={4}>
        <div className={styles.section}>
          <h3>יחסי המרה</h3>
          <div className={styles.fieldsGrid}>
            <div className={styles.fieldGroup}>
              <label>גג</label>
              <InputField
                inputType="number"
                data={convRoof || ""}
                setData={setConvRoof}
                id="conv-roof"
                placeholder="7"
                tooltip={tooltips.convRoof}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label>חזיתות</label>
              <InputField
                inputType="number"
                data={convFacades || ""}
                setData={setConvFacades}
                id="conv-facades"
                placeholder="10"
                tooltip={tooltips.convFacades}
              />
            </div>
          </div>
        </div>
        
        <div className={styles.section}>
          <h3>יחסי ייצור</h3>
          <div className={styles.fieldsGrid}>
            <div className={styles.fieldGroup}>
              <label>גג</label>
              <InputField
                inputType="number"
                data={prodRoof || ""}
                setData={setProdRoof}
                id="prod-roof"
                placeholder="1650"
                tooltip={tooltips.prodRoof}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label>חזיתות</label>
              <InputField
                inputType="number"
                data={prodFacades || ""}
                setData={setProdFacades}
                id="prod-facades"
                placeholder="1300"
                tooltip={tooltips.prodFacades}
              />
            </div>
          </div>
        </div>
      </TabPanel>

      {/* Tab 5: Charging Stations */}
      <TabPanel value={currentTab} index={5}>
        <div className={styles.section}>
          <h3>פרמטרי שירות עמדות</h3>
          <div className={styles.fieldsGrid}>
            <div className={styles.fieldGroup}>
              <label>שירות יומי עמדת AC (רכבים/יום)</label>
              <InputField
                inputType="number"
                data={acStationDailyService || ""}
                setData={setAcStationDailyService}
                id="ac-station-daily"
                placeholder="8"
                tooltip={tooltips.acStationDaily}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label>שירות יומי עמדת DC (רכבים/יום)</label>
              <InputField
                inputType="number"
                data={dcStationDailyService || ""}
                setData={setDcStationDailyService}
                id="dc-station-daily"
                placeholder="24"
                tooltip={tooltips.dcStationDaily}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label>צריכת חשמל יומית לרכב (KWh)</label>
              <InputField
                inputType="number"
                data={dailyVehicleConsumption || ""}
                setData={setDailyVehicleConsumption}
                id="daily-vehicle"
                placeholder="10"
                tooltip={tooltips.dailyVehicle}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label>טעינה ממוצעת (%)</label>
              <InputField
                inputType="number"
                data={averageCharging || ""}
                setData={setAverageCharging}
                id="avg-charging"
                placeholder="50"
                tooltip={tooltips.avgCharging}
              />
            </div>
          </div>
        </div>
        
        <div className={styles.section}>
          <h3>יחסים ופרמטרים</h3>
          <div className={styles.fieldsGrid}>
            <div className={styles.fieldGroup}>
              <label>יחס עמדות AC (0-1) - קריטי לחישובים</label>
              <InputField
                inputType="number"
                data={acStationsRatio || ""}
                setData={setAcStationsRatio}
                id="ac-ratio"
                placeholder="0.7"
                tooltip={tooltips.acRatio}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label>רכבי משתמשים טוענים ברשת מקומית</label>
              <InputField
                inputType="number"
                data={userVehiclesChargingInLocalPublic || ""}
                setData={setUserVehiclesChargingInLocalPublic}
                id="user-vehicles-local"
                placeholder="0"
                tooltip={tooltips.userVehiclesLocal}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label>יחס משתמשים טוענים ברשות מקומית (%)</label>
              <InputField
                inputType="number"
                data={localPublicChargingUsersRatio || ""}
                setData={setLocalPublicChargingUsersRatio}
                id="local-users-ratio"
                placeholder="0"
                tooltip={tooltips.localUsersRatio}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label>יחס יוממים חשמליים לרכבים בעיר</label>
              <InputField
                inputType="number"
                data={electricCommutersRatio || ""}
                setData={setElectricCommutersRatio}
                id="commuters-ratio"
                placeholder="0"
                tooltip={tooltips.commutersRatio}
              />
            </div>
          </div>
        </div>
      </TabPanel>

      <div className={styles.actions}>
        <Button
          id="save-general-parameters"
          text={saving ? "שומר..." : "שמור שינויים"}
          onClick={handleSave}
          loading={saving}
          disabled={saving}
          icon="no"
        />
      </div>
    </div>
  );
};
